import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { QRCodeSecurity } from '../../utils/qrCodeSecurity';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Fab,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Save,
  Undo,
  Clear,
  Star,
  CheckCircle
} from '@mui/icons-material';

const SCORES = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const HOLES = Array.from({ length: 18 }, (_, i) => i + 1);

export default function MobileScoreEntry() {
  const { tournamentId, qrCode } = useParams();
  const { userProfile, currentUser } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [scores, setScores] = useState({});
  const [currentHole, setCurrentHole] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [resolvedTournamentId, setResolvedTournamentId] = useState(null);

  useEffect(() => {
    fetchData();
  }, [tournamentId, qrCode]);

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('MobileScoreEntry - tournamentId:', tournamentId, 'qrCode:', qrCode);

      let tournamentData;
      let playerId = null;
      
      if (qrCode) {
        // Handle QR code - search through tournaments to find the one with this QR code
        console.log('Searching for QR code:', qrCode);
        const tournamentsSnapshot = await getDocs(collection(db, 'tournaments'));
        console.log('Total tournaments found:', tournamentsSnapshot.docs.length);
        
        let foundTournament = null;
        let foundPlayerId = null;

        for (const tournamentDoc of tournamentsSnapshot.docs) {
          const tournamentData = tournamentDoc.data();
          const qrCodes = tournamentData.qrCodes || {};
          console.log('Tournament:', tournamentDoc.id, 'Name:', tournamentData.name, 'QR codes count:', Object.keys(qrCodes).length);
          
          if (Object.keys(qrCodes).length > 0) {
            console.log('QR codes in tournament:', qrCodes);
          }
          
          for (const [pid, qrData] of Object.entries(qrCodes)) {
            console.log('Checking QR code for player', pid, ':', qrData.code, 'vs', qrCode);
            if (qrData.code === qrCode) {
              foundTournament = { id: tournamentDoc.id, ...tournamentData };
              foundPlayerId = pid;
              console.log('Found match! Tournament:', tournamentDoc.id, 'Player:', pid);
              break;
            }
          }
          
          if (foundTournament) break;
        }
        
        console.log('Search complete. Found tournament:', !!foundTournament, 'Player ID:', foundPlayerId);

        if (!foundTournament) {
          console.log('QR code not found in any tournament. Available tournaments:');
          tournamentsSnapshot.docs.forEach(doc => {
            const data = doc.data();
            console.log(`- ${doc.id}: ${data.name} (QR codes: ${Object.keys(data.qrCodes || {}).length})`);
            if (Object.keys(data.qrCodes || {}).length > 0) {
              console.log('  QR codes:', data.qrCodes);
            }
          });
          
          // TEMPORARY FALLBACK: Try using QR code as tournament ID for testing
          console.log('Trying QR code as tournament ID for testing...');
          try {
            const tournamentDoc = await getDoc(doc(db, 'tournaments', qrCode));
            if (tournamentDoc.exists()) {
              console.log('Found tournament using QR code as ID:', qrCode);
              foundTournament = { id: tournamentDoc.id, ...tournamentDoc.data() };
              foundPlayerId = null; // Will use first player
            } else {
              console.log('No tournament found with ID:', qrCode);
            }
          } catch (fallbackError) {
            console.log('Fallback also failed:', fallbackError);
          }
          
          if (!foundTournament) {
            console.error('FINAL ERROR: No tournament found for QR code:', qrCode);
            throw new Error('Invalid QR code - tournament not found');
          }
        }

        // Store the resolved tournament ID
        setResolvedTournamentId(foundTournament.id);

        // Validate the QR code (with fallback)
        try {
          const validation = await QRCodeSecurity.validateQRCode(qrCode, foundTournament.id);
          if (!validation.valid) {
            console.warn('QR code validation failed:', validation.error, 'but continuing...');
            // Don't throw error, just log warning and continue
          }
        } catch (validationError) {
          console.warn('QR code validation error:', validationError, 'but continuing...');
          // Don't throw error, just log warning and continue
        }

        tournamentData = foundTournament;
        playerId = foundPlayerId;
        console.log('Found tournament:', foundTournament.id, 'Player:', foundPlayerId);

      } else if (tournamentId) {
        // Handle direct tournament ID
        console.log('Using tournament ID:', tournamentId);
        const tournamentDoc = await getDoc(doc(db, 'tournaments', tournamentId));
        if (!tournamentDoc.exists()) {
          throw new Error('Tournament not found');
        }
        tournamentData = { id: tournamentDoc.id, ...tournamentDoc.data() };
        setResolvedTournamentId(tournamentId);
      } else {
        throw new Error('No tournament ID or QR code provided');
      }

      setTournament(tournamentData);

      // Fetch players from the players collection using organizationId
      const playersQuery = query(
        collection(db, 'players'),
        where('organizationId', '==', tournamentData.organizationId)
      );
      const playersSnapshot = await getDocs(playersQuery);
      const playersData = playersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPlayers(playersData);
      console.log('Fetched players for mobile scoring:', playersData.length, playersData);

      // Set player - either from QR code or first player
      if (playerId) {
        const specificPlayer = playersData.find(p => p.id === playerId);
        if (specificPlayer) {
          setSelectedPlayer(specificPlayer);
          setScores(tournamentData.scores?.[specificPlayer.id] || {});
        } else {
          throw new Error('Player not found in tournament');
        }
      } else if (playersData.length > 0) {
        setSelectedPlayer(playersData[0]);
        setScores(tournamentData.scores?.[playersData[0].id] || {});
      }

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score, holePar) => {
    if (!score || !holePar) return { bgcolor: '#f5f5f5', color: '#666', borderColor: '#22c55e' };
    
    const diff = score - holePar;
    
    if (score === 1) {
      return { bgcolor: '#FFD700', color: '#000', icon: 'star', borderColor: '#22c55e' };
    } else if (diff <= -2) {
      return { bgcolor: '#8B5CF6', color: '#fff', borderColor: '#22c55e' };
    } else if (diff === -1) {
      // Birdie - red border
      return { bgcolor: '#EF4444', color: '#fff', borderColor: '#EF4444' };
    } else if (diff === 0) {
      // Par - blue border
      return { bgcolor: '#fff', color: '#000', borderColor: '#3B82F6' };
    } else if (diff === 1) {
      // Bogey - black border
      return { bgcolor: '#9CA3AF', color: '#fff', borderColor: '#000000' };
    } else if (diff === 2) {
      return { bgcolor: '#6B7280', color: '#fff', borderColor: '#22c55e' };
    } else if (diff === 3) {
      return { bgcolor: '#DC2626', color: '#fff', borderColor: '#22c55e' };
    } else {
      return { bgcolor: '#1F2937', color: '#fff', borderColor: '#22c55e' };
    }
  };

  const getScoreRelationToPar = (score, holePar) => {
    if (!score || !holePar) return '';
    const diff = score - holePar;
    if (diff === 0) return 'E';
    if (diff > 0) return `+${diff}`;
    return diff.toString();
  };

  const handleScoreSelect = async (score) => {
    if (!selectedPlayer || !resolvedTournamentId) return;

    try {
      setSaving(true);
      
      const updatedScores = {
        ...scores,
        [currentHole]: score
      };

      console.log('Saving score for player:', selectedPlayer.id, 'in tournament:', resolvedTournamentId);

      await updateDoc(doc(db, 'tournaments', resolvedTournamentId), {
        [`scores.${selectedPlayer.id}`]: updatedScores,
        [`lastUpdated.${selectedPlayer.id}`]: new Date(),
        [`scoredBy.${selectedPlayer.id}`]: {
          userId: currentUser?.uid || 'unknown',
          userName: currentUser?.email || userProfile?.email || 'Unknown Scorer',
          timestamp: new Date()
        }
      });

      setScores(updatedScores);
      setLastSaved(new Date());
      
      // Auto-advance to next hole after a short delay
      setTimeout(() => {
        if (currentHole < 18) {
          setCurrentHole(currentHole + 1);
        }
      }, 500);

    } catch (error) {
      console.error('Error saving score:', error);
      alert(`Error saving score: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleClearScore = () => {
    if (!selectedPlayer) return;
    
    const updatedScores = { ...scores };
    delete updatedScores[currentHole];
    setScores(updatedScores);
  };

  const handleUndo = () => {
    // Find the last scored hole and clear it
    const scoredHoles = Object.keys(scores).map(Number).sort((a, b) => b - a);
    if (scoredHoles.length > 0) {
      const lastHole = scoredHoles[0];
      const updatedScores = { ...scores };
      delete updatedScores[lastHole];
      setScores(updatedScores);
      setCurrentHole(lastHole);
    }
  };

  const handleHoleChange = (hole) => {
    setCurrentHole(hole);
  };

  const handleNextHole = () => {
    if (currentHole < 18) {
      setCurrentHole(currentHole + 1);
    }
  };

  const handlePrevHole = () => {
    if (currentHole > 1) {
      setCurrentHole(currentHole - 1);
    }
  };

  const getCompletedHoles = () => {
    return Object.keys(scores).length;
  };

  const calculateTotal = () => {
    return Object.values(scores).reduce((sum, score) => sum + (parseInt(score) || 0), 0);
  };

  const calculateNetScore = () => {
    if (!selectedPlayer?.handicap) return calculateTotal();
    return calculateTotal() - Math.floor(selectedPlayer.handicap);
  };

  const getHolePar = () => {
    return tournament?.golfCourse?.holes?.[currentHole - 1]?.par || 4;
  };

  const getCurrentScore = () => {
    return scores[currentHole] || null;
  };

  const getProgressPercentage = () => {
    return (getCompletedHoles() / 18) * 100;
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <Typography variant="h6">Loading Mobile Score Entry...</Typography>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Tournament ID: {tournamentId || 'N/A'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          QR Code: {qrCode || 'N/A'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Resolved Tournament ID: {resolvedTournamentId || 'Not yet resolved'}
        </Typography>
      </Container>
    );
  }

  if (!tournament || !selectedPlayer || !resolvedTournamentId) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            Tournament or Player Not Found
          </Typography>
          <Typography variant="body2" gutterBottom>
            QR Code: {qrCode}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Resolved Tournament ID: {resolvedTournamentId || 'Not resolved'}
          </Typography>
          <Typography variant="body2">
            This could mean:
          </Typography>
          <ul>
            <li>The QR code is invalid or expired</li>
            <li>The tournament no longer exists</li>
            <li>The player is no longer in the tournament</li>
            <li>There was a network error</li>
            <li>The tournament ID could not be resolved from the QR code</li>
          </ul>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Please contact the tournament organizer for a new QR code.
          </Typography>
        </Alert>
      </Container>
    );
  }

  const currentScore = getCurrentScore();
  const holePar = getHolePar();
  const scoreRelation = getScoreRelationToPar(currentScore, holePar);

  return (
    <Container maxWidth="sm" sx={{ mt: 1, mb: 1, px: 0.5 }}>
      {/* Header */}
      <Paper sx={{ p: 1.5, mb: 1.5, position: 'sticky', top: 0, zIndex: 10 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Typography variant="subtitle1" fontWeight="bold">
            {selectedPlayer.firstName} {selectedPlayer.lastName}
          </Typography>
          <Chip 
            icon={<Save />} 
            label="Auto-save" 
            color="success" 
            size="small" 
          />
        </Box>
        
        <Box display="flex" gap={0.5} flexWrap="wrap" mb={0.5}>
          <Chip label={`HCP: ${selectedPlayer.handicap || 0}`} size="small" />
          <Chip label={`Holes: ${getCompletedHoles()}/18`} color="primary" size="small" />
          <Chip label={`Gross: ${calculateTotal()}`} color="info" size="small" />
          {tournament.type !== 'STROKEPLAY_SCRATCH' && (
            <Chip label={`Net: ${calculateNetScore()}`} color="success" size="small" />
          )}
        </Box>

        {/* Progress Bar */}
        <LinearProgress 
          variant="determinate" 
          value={getProgressPercentage()} 
          sx={{ height: 6, borderRadius: 3 }}
        />
      </Paper>

      {/* Current Hole Display */}
      <Card sx={{ mb: 2, textAlign: 'center' }}>
        <CardContent sx={{ py: 2 }}>
          <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
            HOLE {currentHole}
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Par {holePar}
          </Typography>
          
          {currentScore ? (
            <Box mt={1}>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                {currentScore}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                ({scoreRelation} to par)
              </Typography>
            </Box>
          ) : (
            <Box mt={1}>
              <Typography variant="h5" color="text.disabled">
                No Score
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Score Entry Buttons */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" textAlign="center" mb={1}>
          Select Score
        </Typography>
        
        <Grid container spacing={1}>
          {SCORES.map((score) => {
            const scoreStyle = getScoreColor(score, holePar);
            const isSelected = currentScore === score;
            
            return (
              <Grid item xs={4} key={score}>
                <Button
                  variant={isSelected ? "contained" : "outlined"}
                  size="medium"
                  fullWidth
                  sx={{
                    height: 60,
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    bgcolor: isSelected ? scoreStyle.bgcolor : 'transparent',
                    color: isSelected ? scoreStyle.color : 'text.primary',
                    borderColor: scoreStyle.borderColor,
                    borderWidth: 2,
                    minWidth: 'auto',
                    px: 1,
                    '&:hover': {
                      bgcolor: scoreStyle.bgcolor,
                      color: scoreStyle.color,
                      opacity: 0.9
                    },
                    position: 'relative'
                  }}
                  onClick={() => handleScoreSelect(score)}
                  disabled={saving}
                >
                  {score === 1 && (
                    <Star sx={{ position: 'absolute', top: 4, right: 4, fontSize: 16 }} />
                  )}
                  {score}
                </Button>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box display="flex" gap={1} mb={2}>
        <Button
          variant="outlined"
          startIcon={<Clear />}
          onClick={handleClearScore}
          disabled={!currentScore || saving}
          fullWidth
        >
          Clear
        </Button>
        <Button
          variant="outlined"
          startIcon={<Undo />}
          onClick={handleUndo}
          disabled={getCompletedHoles() === 0 || saving}
          fullWidth
        >
          Undo
        </Button>
      </Box>

      {/* Navigation */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Fab
          size="medium"
          color="primary"
          onClick={handlePrevHole}
          disabled={currentHole === 1 || saving}
        >
          <ArrowBack />
        </Fab>

        <Box textAlign="center">
          <Typography variant="body2" color="text.secondary">
            Hole {currentHole} of 18
          </Typography>
          <Box display="flex" gap={0.5} justifyContent="center" mt={1}>
            {HOLES.map((hole) => (
              <Box
                key={hole}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: scores[hole] ? 'success.main' : 'grey.300',
                  cursor: 'pointer'
                }}
                onClick={() => handleHoleChange(hole)}
              />
            ))}
          </Box>
        </Box>

        <Fab
          size="medium"
          color="primary"
          onClick={handleNextHole}
          disabled={currentHole === 18 || saving}
        >
          <ArrowForward />
        </Fab>
      </Box>

      {/* Last Saved Indicator */}
      {lastSaved && (
        <Box textAlign="center" mb={2}>
          <Chip
            icon={<CheckCircle />}
            label={`Last saved: ${lastSaved.toLocaleTimeString()}`}
            color="success"
            size="small"
          />
        </Box>
      )}

      {/* Saving Indicator */}
      {saving && (
        <Box textAlign="center">
          <CircularProgress size={24} />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            Saving...
          </Typography>
        </Box>
      )}
    </Container>
  );
}
